import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {
  AuthenticateModalContent,
  CreditsPillContent,
  CreditsPillTheme,
  DesignAttachment,
  EditableComponentsMap,
  ImageAttachment,
  MoodType,
  PromptAttachment,
  PromptOption,
  SelectedPromptOptionValues,
  UnselectableEditableComponentsMap,
  UpsellDialogContent,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PromptAttachmentType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPill} from '@Components/ai-resdesign-prompt-box/components/credits-pill';
import styles from './input-box-footer.module.scss';
import {SubmitButton} from '@Components/ai-resdesign-prompt-box/components/submit-button';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {AIRedesignContextualMenu} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-contextual-menu';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import type {AsyncVoidFunction} from '@Utils/general.util';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import type {ImageData} from '@Libraries/add-media-library';
import {useIsSmallerThanBreakpointWidth} from "@Hooks/useIsSmallerThanBreakpoint";
import {ScreenBreakpoints} from "@Libraries/responsiveness-library";


const contextualMenuPanelId = 'ai-contextual-menu-preferences';

interface InputBoxFooterProps {
  showControls: boolean;
  isLoading: boolean;
  isSubmitButtonActive: boolean;
  onSubmission: AsyncVoidFunction;
  creditsPillTheme: CreditsPillTheme;
  promptOptionsToUse: PromptOption[];
  selectedPromptOptionValues: SelectedPromptOptionValues;
  onSelectedPromptOptionValuesChange: (promptOptionValues: SelectedPromptOptionValues) => void;
  editableComponentsMap: EditableComponentsMap;
  onEditableComponentsMapChange: (editableComponentsMap: EditableComponentsMap) => void;
  allowPromptAttachments: boolean;
  addPromptAttachment: (attachment: PromptAttachment) => void;
  upsellDialog?: UpsellDialogContent;
  creditsPillContent?: CreditsPillContent;
  authenticateModalContent?: AuthenticateModalContent;
  unselectableEditableComponents?: UnselectableEditableComponentsMap;
  moodType?: MoodType;
  onContextualMenuOpened?: VoidFunction;
  isEditableDuringGeneration?: boolean;
}

export function InputBoxFooter({creditsPillContent, ...props}: InputBoxFooterProps): ReactElement {
  const footerRef = useRef<HTMLDivElement>(null);
  const contextualMenuDropdownRef = useRef<HTMLDivElement>(null);
  const [contextualMenuWidth, setContextualMenuWidth] = useState<number>(0);
  const [showContextualMenuDropdown, setShowContextualMenuDropdown] = useState(false);
  const showUpsellDialog = props.upsellDialog ? props.upsellDialog.showDialog : false;
  const showAuthenticateModal = props.authenticateModalContent ? props.authenticateModalContent.showModal : false;
  const upsellDialog = props.upsellDialog ? props.upsellDialog.dialogName : undefined;
  const onContextualOptionsClose = useDefaultPanelClose(contextualMenuPanelId);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);
  const CONTEXTUAL_MENU_POPUP_OFFSET = 16;

  useEffect(() => {
    document.addEventListener('touchstart', closeContextualMenuDropdownListener);
    document.addEventListener('mousedown', closeContextualMenuDropdownListener);

    return (): void => {
      document.removeEventListener('touchstart', closeContextualMenuDropdownListener);
      document.removeEventListener('mousedown', closeContextualMenuDropdownListener);
    };
  }, []);

  const updateContextualMenuWidth = (): void => {
    const maxContextualMenuWidth = 280;

    if (isMobile) {
      return;
    }

    if (footerRef.current && !contextualMenuWidth) {
      const width = Math.min(footerRef.current.offsetWidth, maxContextualMenuWidth);
      setContextualMenuWidth(width);
    }
  };

  const closeContextualMenuDropdownListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!contextualMenuDropdownRef.current?.contains(target)) {
      setShowContextualMenuDropdown(false);
    }
  };

  const getContextualMenu = (): ReactElement => {
    return (
      <AIRedesignContextualMenu
        promptOptionsToUse={props.promptOptionsToUse}
        selectedPromptOptionValues={props.selectedPromptOptionValues}
        onSelectedPromptOptionValuesChange={(promptOptionValues: SelectedPromptOptionValues) => {
          props.onSelectedPromptOptionValuesChange(promptOptionValues);
          onContextualOptionsClose();
        }}
        contextualMenuWidth={contextualMenuWidth}
        editableComponentsMap={props.editableComponentsMap}
        onEditableComponentsMapChange={(editableComponentsMap: EditableComponentsMap) => {
          props.onEditableComponentsMapChange(editableComponentsMap);
          onContextualOptionsClose();
        }}
        unselectableEditableComponents={props.unselectableEditableComponents}
        moodType={props.moodType}
      />
    );
  };

  const openMediaSelectorModal = (): void => {
    openMediaTypeModal({
      mediaType: MediaType.IMAGE,
      title: window.i18next.t('pmwjs_select_an_option'),
      maxSelectionNumber: 6,
      onAddSelected: (items: ImageData[]) => {
        if (items.length === 0) {
          return;
        }

        for (const item of items) {
          const imageItem = item;
          const imageAttachment: ImageAttachment = {
            hashedFilename: imageItem.hashedFilename,
            extension: imageItem.extension,
            source: imageItem.source,
            type: PromptAttachmentType.IMAGE,
          };
          props.addPromptAttachment(imageAttachment);
        }
      },
      myDesignSelectedCallback: () => {
        window.PMW.util.require(typeof window.PMW.openDesignSelectorModal === 'undefined', 'designSelector', true, () => {
          window.PMW.setLoading(null);
          window.PMW.openDesignSelectorModal(window.i18next.t('pmwjs_select_a_design'), (designIdMap): void => {
            const designAttachment: DesignAttachment = {
              designId: designIdMap[0].posterId,
              type: PromptAttachmentType.DESIGN,
              previewURL: designIdMap[0].previewUrl,
              pageHashedId: designIdMap[0].pageId,
            };
            props.addPromptAttachment(designAttachment);
          });
        });
      },
    });
  };

  const getAttachmentAdder = (): ReactElement | undefined => {
    if (!props.allowPromptAttachments) {
      return;
    }

    return (
      <>
        <Icon
          icon={'icon-plus'}
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.CIRCLE}
          className={`spacing-p-2 ${styles.footerControlsSelector}`}
          onClick={openMediaSelectorModal}
        />
      </>
    );
  };

  const getContextualMenuDropdown = (): ReactElement | undefined => {
    if (Object.keys(props.editableComponentsMap).length <= 1 && props.promptOptionsToUse.length === 0) {
      return;
    }

    return (
      <div ref={contextualMenuDropdownRef}>
        <Dropdown
          open={showContextualMenuDropdown}
          onOpen={() => {
            updateContextualMenuWidth();
            setShowContextualMenuDropdown(true);
            props.onContextualMenuOpened?.();
          }}
          onClose={() => {
            setShowContextualMenuDropdown(false);
          }}
          doesCloseOnOuterClick
          doCloseOnInnerClick={false}
          selector={<Icon icon={'icon-filter-1'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} className={`spacing-p-2 ${styles.footerControlsSelector}`} />}
          selectorClassNameOnOpen={styles.contextualMenuDropdownSelectorOpen}
          popup={getContextualMenu()}
          popUpHasCustomWidth
          popupClassName={styles.contextualMenuPopup}
          alignment={ALIGNMENT_TYPE.BOTTOM_START}
          shouldShift={false}
          leftOffset={-9}
          popupOffset={CONTEXTUAL_MENU_POPUP_OFFSET}
          showModalForSmallScreen
          position={DROPDOWN_POSITION.FIXED}
          modalTitle={window.i18next.t('pmwjs_preferences')}
          modalClassName={styles.contextualMenuModal}
          panelIdForModal={contextualMenuPanelId}
        />
      </div>
    );
  };

  const getFooterControls = (): ReactElement => {
    return (
      <div className={`flex-row-justify-between flex-align-items-center gap-1`}>
        {getAttachmentAdder()}
        {getContextualMenuDropdown()}
      </div>
    );
  };

  const getCreditsPill = (): ReactElement | undefined => {
    if (props.showControls && (showAuthenticateModal || showUpsellDialog)) {
      return <Icon icon={`icon-crown`} className={styles.premiumCrown} size={IconSize.SIZE_ICON_16} type={IconType.PRIMARY_DARK} />;
    }
    if (props.showControls && creditsPillContent) {
      return <CreditsPill showPill={props.showControls} theme={props.creditsPillTheme} content={creditsPillContent} />;
    }
    return undefined;
  };

  const getCreditsPillAndSubmitButton = (): ReactElement => {
    return (
      <div className={'flex-row-justify-between flex-items-center gap-2'}>
        {getCreditsPill()}
        <SubmitButton
          isLoading={props.isLoading}
          isActive={props.isSubmitButtonActive}
          onSubmission={props.onSubmission}
          showUpsellDialog={showUpsellDialog}
          upsellDialog={upsellDialog}
          authenticateModalContent={props.authenticateModalContent}
          isEditableDuringGeneration={props.isEditableDuringGeneration}
        />
      </div>
    );
  };

  const getFooter = (): ReactElement => {
    return (
      <div ref={footerRef} className={`flex-row-justify-between flex-align-items-center spacing-p-2`}>
        {getFooterControls()}
        {getCreditsPillAndSubmitButton()}
      </div>
    );
  };

  return getFooter();
}
